import { logger } from "@/utils/logger";
import { Module } from "vuex";
import {
  Analysis,
  AnalysisProcessStatusEnum,
  AnalysisSectionDefinition,
  AnalysisState,
  AnswerOption,
  Question,
  RootState
} from "@/types";
import {
  decisionMock,
  getAnalysis,
  getAnalysisAlgorithmeData,
  getAnalysisEvaluatedReserves,
  getAssociatedAnalysisProcess,
  getQuestionsByProcessId,
  getRatiosByProcessId,
  getScoreAnalysis,
  updateAssociatedAnalysisProcess,
  UpdateQuestionnaireGathering, validDocAnalysesProcess, validDocuments
} from "@/store/services/task/actions/analysisService"
import store from "@/store";
import { getGhatheringProcessItem, getHistoryRating, groupAndSortRatings, updateAnalysisFromTask } from '@/store/services/task/taskService';
import Notify from 'quasar/src/plugins/Notify.js';;

import {
  convertRatiodataToRatiovalue,
  deepClone,
  generateQuestionsByCategory,
  generateRatiosByCategory,
  getAnalysesDataRequest,
  initanalysisData
} from '@/utils';
import { faL } from "@fortawesome/free-solid-svg-icons";
import i18n from "@/i18n";
import { sendMessage } from "@/store/services/task/workflow";

let analysisInFlight: Promise<void> | null = null;

/**
 * Merges dynamic keys into the base analysis JSON from initanalysisData without
 * re-running the heavy offer deep-clone in initanalysisData for each algorithm step.
 */
function mergeAnalysisDataPayload(
  baseAnalysisDataJson: string,
  extra: Record<string, unknown>
): string {
  const o = JSON.parse(baseAnalysisDataJson) as Record<string, unknown>;
  return JSON.stringify({ ...o, ...extra });
}

// Function to find items with associated analysis process having resourceUid "65b38958c97daf7ef1474fd9"
function findItemsWithAnalysisProcessUid(decisionSteps: any, analysisProcessUid: string): any {
  const foundItems: any = [];

  for (const decisionStep of decisionSteps) {
    const associatedProcesses = decisionStep.associatedAnalysisProcess;
    for (const process of associatedProcesses) {
      if (process.analysisProcess.resourceUid === analysisProcessUid) {
        foundItems.push(decisionStep);
        break;
      }
    }
  }

  return foundItems;
}

const analysisModule: Module<AnalysisState, RootState> = {
  namespaced: true,
  state: {
    AlgorithmeChanged: false,
    AlgorithmeLoading: false,
    questionnaire: {},
    questionnairegatheringItem: {},
    gatheringQuestions: [],
    selectedAnswersOptions: {},
    ratiosByCategories: {},
    analysis: {
      resourceUid: "",
      objectType: "odm.analysis.process",
      systemUid: "odm-analysis",
      businessData: null,
      daaq: "/",
      reference: "",
      owner: {
        objectType: "odm.user.leaseforgeuser",
        systemUid: "odm-user",
        resourceUid: ""
      },
      analysisEntity: {
        objectType: "odm.decision.decisionprocess",
        systemUid: "odm-decision",
        resourceUid: ""
      },
      analysisData: "",
      analysisScore: {
        resourceUid: "",
        objectType: "odm.analysis.analysisscore",
        systemUid: "odm-analysis"
      },
      status: {
        internalCode: "" as AnalysisProcessStatusEnum,
        defaultValue: true
      },
      assessment: {
        objectType: "odm.analysis.assessment",
        systemUid: "odm-analysis",
        resourceUid: ""
      },
      ratios: [],// in progress
      analysisRatings: [],// in progress
      historyRating: [],
      score: {},
      opinionQualifications: [{
        opinionreserves: [],
        opinion: {
          resourceUid: null
        },
        comment: "",
        opinionQualifications: []
      }]
    },
    reserves: [],
    evaluatedReserves: [],
    associatedAnalysisProcess: {},
    errors:[],
  },
  getters: {

    getOpinionQualifications(state) {
      return state.analysis?.opinionQualifications && (state.analysis?.opinionQualifications.length > 0) ? state.analysis?.opinionQualifications[0].opinionQualifications : []
    },


    getOpinion(state) {
      return state.analysis?.opinionQualifications && (state.analysis?.opinionQualifications.length > 0) ? state.analysis?.opinionQualifications[0].opinion : [];
    },
    getAnalysisStatus(state) {
      return state.analysis?.status?.resourceUid
    },
    analysisSections() {
      return [
        AnalysisSectionDefinition.PRT_IDT,
        AnalysisSectionDefinition.PRT_STU,
        AnalysisSectionDefinition.PRT_PROF,
        AnalysisSectionDefinition.PRT_STF,
        AnalysisSectionDefinition.AST_01,
        AnalysisSectionDefinition.FIN_01,
        AnalysisSectionDefinition.DOC_01,
        AnalysisSectionDefinition.PRT_IDT_COLOC,
        AnalysisSectionDefinition.PRT_IDT_DIST,
        AnalysisSectionDefinition.PRT_IDT_SUP,
      ]
    },
    getAnalysisResourceUid(state) {
      return state.analysis?.resourceUid
    },
    getAlgorithmeLoading(state) {
      return state.AlgorithmeLoading
    }
  },
  actions: {

    getHistoryRating({ commit }, payload) {
      const { filter, callback } = payload
      getHistoryRating(filter)
        .then(res => {
          callback(res)
        })
        .catch(e => logger.error(e))
    },

async analysis({ commit }, payload) {
  if (analysisInFlight) {
    return analysisInFlight;
  }

  const runAnalysis = async () => {
  store.state.analysisModule!.isLoading = true;
  let selectedAnswerOptions: Record<string, AnswerOption> = {};
  const resourceUid = payload.data;
  const isEditing = payload.isEditing;
  const reCalculate = payload.reCalculate;
  let gatheringProcessId = "";
  let ghatheringProcessItem: any = [];
  let analysisData: any = null; // Declare analysisData at the top level
  let result: any = null;
  // Initialize analysisRequest with required properties
  let analysisRequest: Analysis = {};
  const parties = store.state.demandModule?.offer?.associatedParties ?? [];
  const customer = parties.find((p: any) => p?.associatedParty?.role?.resourceUid === "CUSTOMER") ?? null;

  const contextModule = payload.route && payload.route._value.path.includes("/mo/")
    ? (store.state.middleOfficeModule.offer as any)
    : (store.state.taskModule.offer as any);

  // Rest of the functions remain the same...
  const processQuestionnaireData = async (associatedGatheringProcess: any) => {
    const res: any = await getGhatheringProcessItem(associatedGatheringProcess.gatheringProcess.resourceUid);
    if (res.contextType.rawValue !== "QUESTIONNAIRE") return null;

    const questionnaireItems = res.gatheringProcessItem.filter(
      (item: any) => item.objectType.includes('questionnairegathering')
    );

    if (!questionnaireItems.length) return {};

    const processQuestions = await getQuestionsByProcessId(associatedGatheringProcess.gatheringProcess.resourceUid);
    const processQuestionsByCatgory = generateQuestionsByCategory(processQuestions);

    return {
      processQuestionsByCatgory,
      questionnaireItem: questionnaireItems[0],
      gatheringProcessId: associatedGatheringProcess.gatheringProcess.resourceUid
    };
  };

  const processAnswers = (questionnaireData: any, questions: any[]) => {
    const answers: Record<string, AnswerOption> = {};

    const findMatchingQuestion = (categoryId: string, questionId: string) => {
      const category = questionnaireData.processQuestionsByCatgory[categoryId];
      if (!category) return null;

      for (const key in category) {
        const question = category[key];
        if (question.question_id === questionId) {
          return question;
        }
      }
      return null;
    };

    for (const questionData of questions) {
      const questionId = questionData.question.resourceUid;
      const answerId = questionData.answer.resourceUid;

      for (const categoryKey in questionnaireData.processQuestionsByCatgory) {
        const questionInfo = findMatchingQuestion(categoryKey, questionId);
        if (!questionInfo) continue;

        let answerAccordingBusinessData = null
        if(questionId === "Question_54" && customer && customer.associatedParty?.third?.businessData?.renewal === 1){
          answerAccordingBusinessData = {
            "answer_option_order": 1,
            "answer_option_id": "A49_YES",
            "answer_option_i18n_code": "QUEST.ANSWER.A49",
            "question_id": "Question_54"
          }
        }
        else if(questionId === "Question_54" && customer && customer.associatedParty?.third?.businessData?.renewal === 0){
          answerAccordingBusinessData = {
            "answer_option_order": 0,
            "answer_option_id": "A50_NO",
            "answer_option_i18n_code": "QUEST.ANSWER.A50",
            "question_id": "Question_54"
          }
        }
        
        if(questionId === "Question_55" && customer && customer.associatedParty?.third?.businessData?.isCfClient === 1) {
          answerAccordingBusinessData = {
            "answer_option_order": 1,
            "answer_option_id": "A49_YES",
            "answer_option_i18n_code": "QUEST.ANSWER.A49",
            "question_id": "Question_55"
          }
        }
        else if(questionId === "Question_55" && customer && customer.associatedParty?.third?.businessData?.isCfClient === 0){
          answerAccordingBusinessData = {
            "answer_option_order": 0,
            "answer_option_id": "A50_NO",
            "answer_option_i18n_code": "QUEST.ANSWER.A50",
            "question_id": "Question_55"
          }
        }

        const answerInfo = answerAccordingBusinessData ? answerAccordingBusinessData : questionInfo.answerOptions.find(
          (opt: any) => opt.answer_option_id === answerId
        );

        if (answerInfo) {
          answers[questionInfo.question_i18n_code] = answerInfo;
          break;
        }
      }
    }
    return answers;
  };

  try {
    if (!reCalculate) {
      const maxRetries = 3;
      let retryCount = 0;
      let questionnaireData = null;

      while (retryCount < maxRetries && !questionnaireData) {
        for (const associatedGatheringProcess of contextModule?.associatedGatheringProcess) {
          questionnaireData = await processQuestionnaireData(associatedGatheringProcess);
          if (questionnaireData) break;
        }

        if (!questionnaireData) {
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (questionnaireData && Object.keys(questionnaireData).length > 0) {
        commit('setQueastionnaire', questionnaireData.processQuestionsByCatgory);
        commit('setGatheringProcessItem', {
          objectType: "odm.supportingdocument.gatheringprocessitem.questionnairegathering",
          resourceUid: questionnaireData.questionnaireItem.resourceUid,
        });
        commit('setAnalysisQuestions', questionnaireData.questionnaireItem.questions);

        const answers = processAnswers(questionnaireData, questionnaireData.questionnaireItem.questions);
        commit('setAnalysisQuestionsAnswers', answers);
      }
    }

    const waitForGatheringQuestions = async (maxWaitTime = 10000) => {
      const startTime = Date.now();
      while (!store.state.analysisModule?.gatheringQuestions && (Date.now() - startTime) < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!store.state.analysisModule?.gatheringQuestions) {
        throw new Error('Failed to load questionnaire data');
      }
    };

    await waitForGatheringQuestions();

    store.state.analysisModule!.AlgorithmeChanged = false;
    analysisData = await initanalysisData(contextModule, {}); // Assign to the top-level variable
    const res = await getAnalysis(resourceUid);
    result = deepClone(res);

    analysisRequest = {
      ...analysisRequest,
      ...res
    };
    const offer = store.state.taskModule.offer || store.state.middleOfficeModule.offer;
    
    const customerOrganization = (offer!.associatedParties!.find((partyObj: any) => partyObj?.associatedParty?.role?.resourceUid === 'CUSTOMER'
  )?.associatedParty?.third?.type?.id)!.includes('FrenchOrganization')
    const ratiosByCatgories = await getRatiosByProcessId(resourceUid,customerOrganization);
    commit('setRatiosByProcessId', generateRatiosByCategory(ratiosByCatgories));
    //logger.debug("store.state.analysisModule?.gatheringQuestions---------------", store.state.analysisModule?.gatheringQuestions)
    const questionnaire = JSON.stringify(store.state.analysisModule?.gatheringQuestions);

    if (!isEditing) {
      // One initanalysisData (line above); merge JSON for each HTTP step — avoids 4x deep-clone
      const payloadWithQuestionnaire = mergeAnalysisDataPayload(analysisData, {
        questionnaire
      });
      
      const calculateRatio = await getAnalysisAlgorithmeData(
        resourceUid,
        getAnalysesDataRequest(payloadWithQuestionnaire),
        "calculateRatio"
      );

      const payloadAfterCalculateRatio = mergeAnalysisDataPayload(payloadWithQuestionnaire, {
        ratiosData: JSON.stringify(calculateRatio.ratios)
      });
      const evaluateRatio = await getAnalysisAlgorithmeData(
        resourceUid,
        getAnalysesDataRequest(payloadAfterCalculateRatio),
        "evaluateRatio"
      );

      const payloadAfterEvaluateRatio = mergeAnalysisDataPayload(payloadAfterCalculateRatio, {
        ratiosData: JSON.stringify(evaluateRatio.ratios)
      });
      const evaluateRating = await getAnalysisAlgorithmeData(
        resourceUid,
        getAnalysesDataRequest(payloadAfterEvaluateRatio),
        "evaluateRating"
      );

      const noteLab = analysisRequest.analysisRatings?.find(rating => rating.type?.toLowerCase() === "notelab");

      if (noteLab?.comment) {
        const noteLabInEvaluateRating = evaluateRating.ratings.find((rating:any) => rating.type.toLowerCase() === "notelab");
        if (noteLabInEvaluateRating) {
          noteLabInEvaluateRating.comment = noteLab.comment;
        }
      }

      const filtredEvaluatedRating = evaluateRating.ratings;

      // Match original: only base + questionnaire + ratingsData (no ratiosData in payload)
      const payloadForScore = mergeAnalysisDataPayload(analysisData, {
        questionnaire,
        ratingsData: JSON.stringify(evaluateRating.ratings)
      });
      const evaluateScore = await getAnalysisAlgorithmeData(
        resourceUid,
        getAnalysesDataRequest(payloadForScore),
        "evaluateScore"
      );

      const score = await getScoreAnalysis(evaluateScore.analysisScore.resourceUid);

      analysisRequest = {
        ...analysisRequest,
        analysisData,
        ratios: convertRatiodataToRatiovalue(calculateRatio.ratios),
        historyRating: groupAndSortRatings(evaluateRating.analysisRatings),
        analysisRatings: filtredEvaluatedRating.filter((rating: any) => !rating.validity || !rating.validity.until),
        analysisScore: evaluateScore.analysisScore,
        score
      };

      commit("setAnalysisCamundaVaiable", {
        evaluateRatios: evaluateRatio.ratios,
        evaluateRatings: filtredEvaluatedRating,
        evaluateScore: score
      });
    } else {
      analysisRequest = {
        ...analysisRequest,
        analysisData,
        ratios: convertRatiodataToRatiovalue(result.ratios),
        historyRating: groupAndSortRatings(result.analysisRatings),
        analysisRatings: result.analysisRatings.filter((rating: any) => !rating.validity.until),
        analysisScore: result.analysisScore,
        score: result.score
      };
    }
  } catch (error) {
    store.state.analysisModule!.errors.algo = "Analysis.errors.algo";
    logger.error('Analysis error:', error);
    throw error;
  } finally {
    commit("setAnalysis", analysisRequest);
    const nestedAnalysis = store.state.taskModule?.analysisModule?.analysis;
    if (store.state.analysisModule) {
      store.state.analysisModule.analysis = nestedAnalysis
        ? deepClone(nestedAnalysis)
        : deepClone(analysisRequest);
    }
    store.state.analysisModule!.isLoading = false;
    try {
      const analysisEvaluatedReserves = await getAnalysisEvaluatedReserves({ data: analysisData });
      commit("setEvaluatedReserves", analysisEvaluatedReserves.map((elem: any) => ({ ...elem, comment: "" })));

      const AssociatedAnalysisProcessList = payload.route?._value.path.includes("/mo/")
        ? findItemsWithAnalysisProcessUid(store.state.middleOfficeModule.decisionStep, resourceUid)[0]
        : payload.fetchedDecision ? payload.fetchedDecision[0] : await getAssociatedAnalysisProcess(result.analysisEntity.resourceUid);

      const AssociatedAnalysisProcess = AssociatedAnalysisProcessList.associatedAnalysisProcess
        .filter((item: any) => item.analysisProcess.resourceUid === resourceUid);

      commit("setAssociatedAnalysisProcess", AssociatedAnalysisProcess[0]);

      if (AssociatedAnalysisProcess[0]?.reserves) {
        const payloadRequest = AssociatedAnalysisProcess[0].reserves.map((elem: any) => {
          const reserveName = store.state.analysisModule?.evaluatedReserves
            ?.find(reserve => reserve.resourceUid === elem.reserve.resourceUid)?.reserveName;

          return {
            businessData: null,
            comment: elem.comment,
            daaq: null,
            objectType: "odm.decision.reservedata",
            reserveName,
            resourceUid: elem.reserve.resourceUid,
            systemUid: "odm-decision",
          };
        });

        commit('setAnalysisReserves', payloadRequest);
      }
    } catch (error) {
      logger.error('Reserves evaluation error:', error);
    }
  }
  };

  analysisInFlight = runAnalysis().finally(() => {
    analysisInFlight = null;
  });
  return analysisInFlight;
},



    updateAnalysisReserves({ commit }, payload) {
      commit('setAnalysisReserves', payload)
    },
    updateAnalysisState({ commit }, payload) {
      const start = AnalysisProcessStatusEnum.START
      const hold = AnalysisProcessStatusEnum.ONHOLD
      if (this.state?.analysisModule?.analysis?.status?.resourceUid) {
        logger.debug("this.state.analysisModule.analysis.status.resourceUid ",this.state.analysisModule.analysis.status.resourceUid)
        this.state.analysisModule.analysis.status.resourceUid = payload;
        if (payload === AnalysisProcessStatusEnum.IN_PROGRESS ){store.dispatch("taskModule/setTaskStatus", start)}
        else if (payload === AnalysisProcessStatusEnum.ON_HOLD ){store.dispatch("taskModule/setTaskStatus", hold)}
        
      }
      this.dispatch("analysisModule/updateAnalysis")
    },
    updateAnalysis({ commit }) {
      
      const analysis = this.state.analysisModule?.analysis!
      const gatheringQuestionsState = store.state.analysisModule?.gatheringQuestions
      const answersState = store.state.analysisModule?.selectedAnswersOptions
      const updatedAnswers: Question[] = gatheringQuestionsState!.map((item) => {
        const questionId = item.question.resourceUid;
        const answerData: any = Object.values(answersState as any).find((data: any) => data.question_id === questionId);

        if (answerData) {
          // Update the answer resourceUid
          item.answer.resourceUid = answerData.answer_option_id;
        }
        return item;
      });

      const getAssociatedAnalysisProcessRequest = () => {
        const associated = deepClone(
          store.state.analysisModule?.associatedAnalysisProcess
        );
        const previousReserves = deepClone(associated?.reserves || []);
        const selectedReserves = deepClone(store.state.analysisModule?.reserves || []);

        const request: any[] = [];

        selectedReserves.forEach((elem: any, index: number) => {
          let found = previousReserves.find(
            (r: any) =>
              r.reserve?.resourceUid === elem.resourceUid &&
              r.comment === elem.comment
          );

          let resultReserve: any;

          if (found) {
            resultReserve = deepClone(found);
            resultReserve.comment = elem.comment || "";
          } else {
            resultReserve = {
              objectType: "odm.decision.elementreserve.analysisreserve",
              systemUid: "odm-decision",
              businessData: null,
              daaq: "/",
              comment: elem.comment || "",
              reserve: {
                objectType: "odm.decision.reserve",
                systemUid: "odm-decision",
                resourceUid: elem.resourceUid,
              },
            };
          }

          request.push(resultReserve);
        });

        associated.businessData = {
          assignee: `${store.state.authModule?.user.firstName} ${store.state.authModule?.user.lastName}`,
        };

        delete associated.analysisProcessReference
        delete associated.analysisProcessResourceUid
        
        return {
          ...associated,
          reserves: request,
        };
      };


      updateAssociatedAnalysisProcess(getAssociatedAnalysisProcessRequest())
        .then(response => {
          if (Object.keys(store.state.analysisModule?.questionnairegatheringItem || {}).length === 0) {
            // Object is empty
            updateAnalysisFromTask(analysis, store.state.demandModule.offer?.resourceUid)
              .then(response => {

                this.dispatch('analysisModule/analysis', { data: response.resourceUid, isEditing: false, reCalculate: false });

              })
              .catch(err => {
                logger.error(err)
                Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                  message: err.message || err.error || err,
                  color: 'negative'
                });
              })


          } else {
            // Object is not empty

            UpdateQuestionnaireGathering(updatedAnswers, store.state.analysisModule?.questionnairegatheringItem)
              .then(response => {
                updateAnalysisFromTask(analysis, store.state.demandModule.offer?.resourceUid)
                  .then(response => {

                    this.dispatch('analysisModule/analysis', { data: response.resourceUid, isEditing: false, reCalculate: false });

                  })
                  .catch(err => {
                    logger.error(err)
                    Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                      message: err.message || err.error || err,
                      color: 'negative'
                    });
                  })
              })
              .catch(err => {
                logger.error(err)
                Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                  message: err.message || err.error || err,
                  color: 'negative'
                });
              })
          }

        }).catch(err => {
          logger.error(err)
          Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
            message: err.message || err.error || err,
            color: 'negative'
          });
        })
    },
    setInitialOpinionQualificationsState({ commit }, payload) {
      commit("setInitialOpinionQualificationsState", payload)
    },

    setAnalysis({ commit }, payload) {

      commit("setAnalysis", payload)
    },
    cleanState({ commit }) {
      commit("setInitial");
    },
    initAnalysisState({ commit }) {
      analysisInFlight = null;
      commit("initAnalysisState")
    },
    listDecisioMock(context, payload) {
      const { callback } = payload
      decisionMock()
        .then(response => {
          callback(response)
        })
        .catch(err => logger.error(err));
    },
    setOpinionReserves({ commit }, payload) {
      commit('setOpinionReserves', payload)
    },

    validDocuments({ commit }, payload) {
      validDocAnalysesProcess().catch(() => logger.error(i18n.global.t("demand.documents.valid")))
    },
    sendMessage({ commit }, payload) {
      const { messageTosend,resourceUid,callback} = payload
      store.state.analysisModule!.isLoading = true;
      sendMessage(messageTosend)
        .then(async res => {
          const analysis = await getAnalysis(resourceUid);
          if (this.state?.analysisModule?.analysis?.status?.resourceUid) {
            this.state.analysisModule.analysis.status.resourceUid = analysis.status.resourceUid;
          }
          store.state.analysisModule!.isLoading = false;
          callback(res);
        })
        .catch(e => {
          logger.error(e);
          store.state.analysisModule!.isLoading = false;
        })
    },
    updateAnalysisRating({ commit }: { commit: any }, payload: { index: number; rating: any }) {
      commit('updateAnalysisRating', payload);
    }

  },
  mutations: {

    setInitial(state) {
      //       state.analysis = {
      // };

    },
    setRatiosByProcessId(state, payload) {
      state.ratiosByCategories = payload
    },
    setAnalysisReserves(state, payload) {
      state.reserves = payload
    },
    setEvaluatedReserves(state, payload) {
      state.evaluatedReserves = payload
    },
    setGatheringProcessItem(state, payload) {
      state.questionnairegatheringItem = payload
    },
    setAnalysisQuestions(state, payload) {
      state.gatheringQuestions = payload
    },
    setQueastionnaire(state, payload) {
      state.questionnaire = payload
    },
    setAnalysisQuestionsAnswers(state, payload) {
      state.selectedAnswersOptions = payload
      state.AlgorithmeChanged = false
    },
    setAnalysisCamundaVaiable(state,payload) {
      state.analysisDataInfo = payload
      if (store.state.analysisModule && store.state.analysisModule !== state) {
        store.state.analysisModule.analysisDataInfo = deepClone(payload);
      }
    },
    setAlgorithmeLoading(state,payload) {
      state.AlgorithmeLoading = payload
    },
    setAnalysis(state, payload) {
      if (!payload) {
        state.analysis = payload;
        return;
      }

      const createDefaultOpinionWrapper = () => [{
        opinion: { resourceUid: null },
        comment: "",
        opinionQualifications: [] as any[],
      }];

      const cloneOpinionWrapper = (source: unknown) => {
        if (!Array.isArray(source) || source.length === 0) {
          return createDefaultOpinionWrapper();
        }
        try {
          return deepClone(source as object) as ReturnType<typeof createDefaultOpinionWrapper>;
        } catch {
          return createDefaultOpinionWrapper();
        }
      };

      const previousWrapper = cloneOpinionWrapper(state.analysis?.opinionQualifications)
        ?? cloneOpinionWrapper(store.state.analysisModule?.analysis?.opinionQualifications);

      state.analysis = payload;
      const apiOpinionQualifications = state.analysis?.opinionQualifications;

      if (
        state.analysis?.status?.resourceUid === "CREATED" &&
        Array.isArray(apiOpinionQualifications) &&
        apiOpinionQualifications.length === 0
      ) {
        state.analysis.opinionQualifications = createDefaultOpinionWrapper();
        const sectionsArray =
          store.getters["taskModule/analysisModule/analysisSections"] ??
          store.getters["analysisModule/analysisSections"] ??
          [];
        sectionsArray.forEach((element: any) => {
          state.analysis!.opinionQualifications![0].opinionQualifications.push({
            comment: "",
            qualificationType: { resourceUid: element },
          });
        });
        return;
      }

      if (Array.isArray(apiOpinionQualifications) && apiOpinionQualifications.length > 0) {
        const wrapper = previousWrapper?.[0] ? previousWrapper : createDefaultOpinionWrapper();
        wrapper[0].opinionQualifications = apiOpinionQualifications;
        state.analysis!.opinionQualifications = wrapper;
      }
    },

    // opinionMaker:store.state.authModule?.user.userId

    setComment(state, { index, text }) {
      state.analysis!.opinionQualifications[0].opinionQualifications[index].comment = text
    },
    setGlobaleOpinion(state, { resourceUid }) {

      state.analysis!.opinionQualifications[0].opinion = { resourceUid }
    },
    setGlobaleComment(state, payload) {

      state.analysis!.opinionQualifications[0].comment = payload
    },
    initAnalysisState(state) {
      state.questionnaire = {}
      state.questionnairegatheringItem = {}
      state.gatheringQuestions = []
      state.selectedAnswersOptions = {}
      state.analysisDataInfo = undefined
      state.analysis = {
        resourceUid: "",
        objectType: "odm.analysis.process",
        systemUid: "odm-analysis",
        businessData: null,
        daaq: "/",
        reference: "",
        owner: {
          objectType: "odm.user.leaseforgeuser",
          systemUid: "odm-user",
          resourceUid: ""
        },
        analysisEntity: {
          objectType: "odm.decision.decisionprocess",
          systemUid: "odm-decision",
          resourceUid: ""
        },
        analysisData: "",
        analysisScore: {
          resourceUid: "",
          objectType: "odm.analysis.analysisscore",
          systemUid: "odm-analysis"
        },
        status: {
          internalCode: "" as AnalysisProcessStatusEnum,
          defaultValue: true
        },
        assessment: {
          objectType: "odm.analysis.assessment",
          systemUid: "odm-analysis",
          resourceUid: ""
        },
        ratios: [],// in progress
        analysisRatings: [],// in progress
        historyRating: [],
        score: {},
        opinionQualifications: [{

          opinion: {
            resourceUid: null
          },
          comment: "",
          opinionQualifications: []
        }]
      }
      state.reserves = []
      state.evaluatedReserves = []
      state.associatedAnalysisProcess = {}
    },

    setAssociatedAnalysisProcess(state, payload) {
      state.associatedAnalysisProcess = payload
    },
    setOpinionReserves(state, payload) {
      state.opinionreserves = payload
    },
    setRatingHistory(state, payload) {
      if (state.analysis)
        state.analysis.historyRating = payload
    },
    updateAnalysisRating(state: any, { index, rating }: { index: number; rating: any }) {
      if (state.analysis && state.analysis.analysisRatings) {
        state.analysis.analysisRatings.splice(index, 1, rating);
      }
    }


  },
};

export default analysisModule;
